import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSectionSelect } from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type EligibleSection = {
  id: string;
  name: string;
  capacity: number;
  enrolled_count: number;
  seats_available: number;
};

type EnrollmentDetail = {
  id: string;
  enrollment_no: string;
  status: string;
  record_version: number;
  student_id: string;
  session_id: string;
  grade_level_id: string;
  effective_on?: string | null;
  student?: { admission_no?: string; first_name?: string; last_name?: string | null };
  session?: { code?: string; name?: string };
  grade?: { code?: string; name?: string };
  current_section?: { section_id?: string; section_name?: string } | null;
  eligible_sections?: EligibleSection[];
  allocation_history?: Array<{
    id: string;
    section_name?: string;
    effective_on?: string;
    ended_on?: string | null;
    reason?: string | null;
    is_current?: number | boolean;
  }>;
  can_allocate?: boolean;
  allocate_blocked_reason?: string | null;
};

export default function SectionAllocationPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const back = `/education/enrollments/${id}?${params.toString()}`;

  const enrollment = useQuery({
    queryKey: ['education-enrollment', id],
    enabled: Boolean(id),
    queryFn: () => api.get<EnrollmentDetail>(`/education/enrollments/${id}`),
  });

  const detail = enrollment.data?.data;
  const studentName = [detail?.student?.first_name, detail?.student?.last_name].filter(Boolean).join(' ');

  useEffect(() => {
    if (detail?.record_version != null) form.setFieldValue('record_version', detail.record_version);
  }, [detail?.record_version, form]);

  const allocate = useMutation({
    mutationFn: (values: { section_id: string; record_version: number }) =>
      api.post(`/education/enrollments/${id}/allocate`, {
        section_id: values.section_id,
        record_version: values.record_version ?? detail?.record_version,
      }),
    onSuccess: () => {
      message.success(t('sectionAllocated'));
      queryClient.invalidateQueries({ queryKey: ['education-enrollment', id] });
      queryClient.invalidateQueries({ queryKey: ['education-lookup-sections'] });
      navigate(back);
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const sections = detail?.eligible_sections ?? [];
  const blocked = Boolean(detail && !detail.can_allocate);

  return (
    <EducationPageChrome
      title={t('allocateSection')}
      description={t('allocationDetailIntro', {
        defaultValue:
          'Assign the student to a class section for this academic session and grade. Capacity is enforced. Enrollment must be confirmed first.',
      })}
      tags={detail ? <Tag color={detail.can_allocate ? 'green' : 'orange'}>{detail.status}</Tag> : undefined}
      extra={<Button onClick={() => navigate(back)}>{t('backToEnrollments')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={enrollment.isLoading}>
          <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('enrollmentNo')}>{detail?.enrollment_no || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('status')}>
              {detail?.status ? <Tag>{detail.status}</Tag> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('student')}>
              {studentName || '—'}
              {detail?.student?.admission_no ? ` (${detail.student.admission_no})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label={t('session')}>
              {detail?.session?.name || detail?.session?.code || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('grade')}>
              {detail?.grade?.name || detail?.grade?.code || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('currentSection', { defaultValue: 'Current section' })}>
              {detail?.current_section?.section_name || t('notAllocated', { defaultValue: 'Not allocated' })}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {blocked && detail?.allocate_blocked_reason ? (
          <Alert
            type="warning"
            showIcon
            message={t('cannotAllocate', { defaultValue: 'Cannot allocate yet' })}
            description={detail.allocate_blocked_reason}
            action={
              <Space direction="vertical">
                {detail.current_section ? (
                  <Button size="small" type="primary" onClick={() => navigate(`/education/enrollments/${id}/transfer?${params.toString()}`)}>
                    {t('transferSection')}
                  </Button>
                ) : null}
                {!sections.length ? (
                  <Button size="small" onClick={() => navigate(`/education/academic/sessions/${detail.session_id}?panel=sections`)}>
                    {t('openAcademicSession', { defaultValue: 'Open academic session' })}
                  </Button>
                ) : null}
              </Space>
            }
          />
        ) : null}

        <Card size="small" title={t('eligibleSections', { defaultValue: 'Eligible sections (this grade & session)' })}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={sections}
            locale={{
              emptyText: t('noEligibleSections', {
                defaultValue: 'No matching sections — create them under Academic Sessions for this grade',
              }),
            }}
            columns={[
              { title: t('section'), dataIndex: 'name' },
              { title: t('capacity'), dataIndex: 'capacity', width: 100 },
              { title: t('enrolled', { defaultValue: 'Enrolled' }), dataIndex: 'enrolled_count', width: 100 },
              {
                title: t('seatsLeft', { defaultValue: 'Seats left' }),
                dataIndex: 'seats_available',
                width: 110,
                render: (v: number) => <Tag color={v > 0 ? 'green' : 'red'}>{v}</Tag>,
              },
            ]}
          />
        </Card>

        <Card size="small" title={t('allocateSection')}>
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => allocate.mutate(values)}
            style={{ maxWidth: 520 }}
            disabled={blocked}
          >
            <Form.Item name="section_id" label={t('section')} rules={[{ required: true }]}>
              <EducationSectionSelect
                sessionId={detail?.session_id}
                gradeLevelId={detail?.grade_level_id}
                onlyWithSeats
              />
            </Form.Item>
            <Form.Item name="record_version" hidden>
              <Input />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={allocate.isPending} disabled={blocked}>
              {t('allocateSection')}
            </Button>
          </Form>
        </Card>

        {(detail?.allocation_history?.length ?? 0) > 0 ? (
          <Card size="small" title={t('allocationHistory', { defaultValue: 'Allocation history' })}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail?.allocation_history}
              columns={[
                { title: t('section'), dataIndex: 'section_name' },
                { title: t('effectiveOn'), dataIndex: 'effective_on', width: 140 },
                { title: t('endedOn', { defaultValue: 'Ended on' }), dataIndex: 'ended_on', width: 140 },
                { title: t('reason'), dataIndex: 'reason', ellipsis: true },
                {
                  title: t('status'),
                  dataIndex: 'is_current',
                  width: 100,
                  render: (v: number | boolean) =>
                    v ? <Tag color="green">{t('current', { defaultValue: 'Current' })}</Tag> : <Tag>{t('past', { defaultValue: 'Past' })}</Tag>,
                },
              ]}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
